<template>
  <div class="reminder-list">
    <!-- Header -->
    <div class="list-header">
      <div class="filtros">
        <select v-model="filtros.tipo" class="select-filtro" @change="carregarLista">
          <option value="">Todos os tipos</option>
          <option value="aniversario">Aniversário</option>
          <option value="boas_vindas">Boas-vindas</option>
          <option value="inatividade">Inatividade</option>
          <option value="recorrente">Recorrente</option>
        </select>
        <select v-model="filtros.canal" class="select-filtro" @change="carregarLista">
          <option value="">Todos os canais</option>
          <option value="email">Email</option>
          <option value="whatsapp">WhatsApp</option>
        </select>
        <select v-model="filtros.ativo" class="select-filtro" @change="carregarLista">
          <option value="">Todos os status</option>
          <option value="true">Ativos</option>
          <option value="false">Inativos</option>
        </select>
      </div>
      <button class="btn-criar" @click="$emit('criar')">
        <i class="fas fa-plus"></i> Novo Lembrete
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <span>Carregando lembretes...</span>
    </div>

    <!-- Lista vazia -->
    <div v-else-if="lembretes.length === 0" class="empty-state">
      <i class="fas fa-bell"></i>
      <p>Nenhum lembrete automático configurado</p>
      <button class="btn-criar-empty" @click="$emit('criar')">
        Criar primeiro lembrete
      </button>
    </div>

    <!-- Grid de lembretes -->
    <div v-else class="lembretes-grid">
      <div
        v-for="lembrete in lembretes"
        :key="lembrete.id"
        class="lembrete-card"
        :class="{ inativo: !lembrete.ativo }"
      >
        <div class="card-header">
          <span class="tipo-badge" :style="getTipoStyle(lembrete.tipo)">
            <i :class="getTipoIcon(lembrete.tipo)"></i>
            {{ getTipoLabel(lembrete.tipo) }}
          </span>
          <label class="toggle-switch" :title="lembrete.ativo ? 'Desativar' : 'Ativar'">
            <input
              type="checkbox"
              :checked="lembrete.ativo"
              @change="$emit('toggle', lembrete)"
            />
            <span class="toggle-slider"></span>
          </label>
        </div>

        <h3 class="lembrete-nome">{{ lembrete.nome }}</h3>

        <p v-if="lembrete.descricao" class="lembrete-descricao">
          {{ lembrete.descricao }}
        </p>

        <div class="lembrete-info">
          <div class="info-row">
            <i class="fas fa-file-alt"></i>
            <span>{{ lembrete.template?.nome || 'Template não definido' }}</span>
          </div>
          <div class="info-row">
            <i :class="lembrete.canal === 'email' ? 'fas fa-envelope' : 'fab fa-whatsapp'"></i>
            <span>{{ lembrete.canal === 'email' ? 'Email' : 'WhatsApp' }}</span>
          </div>
          <div class="info-row">
            <i class="fas fa-clock"></i>
            <span>{{ getDescricaoConfiguracao(lembrete) }}</span>
          </div>
          <div class="info-row">
            <i class="fas fa-users"></i>
            <span>{{ lembrete.total_destinatarios_base || 0 }} destinatários base</span>
          </div>
        </div>

        <!-- Estatísticas de hoje -->
        <div v-if="lembrete.ativo" class="stats-hoje">
          <div class="stat-item success">
            <i class="fas fa-check"></i>
            <span>{{ lembrete.total_enviados_hoje || 0 }}</span>
          </div>
          <div v-if="lembrete.total_falhas_hoje > 0" class="stat-item error">
            <i class="fas fa-times"></i>
            <span>{{ lembrete.total_falhas_hoje }}</span>
          </div>
        </div>

        <!-- Último processamento -->
        <div v-if="lembrete.ultimo_processamento" class="ultimo-processamento">
          <i class="fas fa-history"></i>
          <span>Último: {{ formatarData(lembrete.ultimo_processamento) }}</span>
        </div>

        <div class="card-actions">
          <button class="btn-action" title="Editar" @click="$emit('editar', lembrete)">
            <i class="fas fa-edit"></i>
          </button>
          <button class="btn-action" title="Ver logs" @click="$emit('logs', lembrete)">
            <i class="fas fa-history"></i>
          </button>
          <button
            class="btn-action btn-danger"
            title="Excluir"
            @click="confirmarExclusao(lembrete)"
          >
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Swal from 'sweetalert2'
import dayjs from 'dayjs'
import { useNotificationReminders } from '@/composables/useNotificationReminders'

const props = defineProps({
  lembretes: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
})

const emit = defineEmits(['criar', 'editar', 'excluir', 'toggle', 'logs', 'carregar'])

const { tiposConfig, getDescricaoConfiguracao } = useNotificationReminders()

const filtros = ref({
  tipo: '',
  canal: '',
  ativo: '',
})

function carregarLista() {
  emit('carregar', filtros.value)
}

function getTipoIcon(tipo) {
  return tiposConfig[tipo]?.icon || 'fas fa-bell'
}

function getTipoLabel(tipo) {
  return tiposConfig[tipo]?.label || tipo
}

function getTipoStyle(tipo) {
  const config = tiposConfig[tipo]
  if (!config) return {}
  return {
    background: config.corBg,
    color: config.cor,
  }
}

function formatarData(data) {
  if (!data) return '-'
  return dayjs(data).format('DD/MM/YYYY HH:mm')
}

async function confirmarExclusao(lembrete) {
  const result = await Swal.fire({
    title: 'Excluir lembrete?',
    text: `O lembrete "${lembrete.nome}" será excluído permanentemente.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc3545',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Sim, excluir',
    cancelButtonText: 'Cancelar',
  })

  if (result.isConfirmed) {
    emit('excluir', lembrete)
  }
}

onMounted(() => {
  carregarLista()
})
</script>

<style scoped>
.reminder-list {
  padding: 20px;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
}

.filtros {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.select-filtro {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  cursor: pointer;
}

.btn-criar {
  padding: 10px 20px;
  background: #4caf50;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s;
}

.btn-criar:hover {
  background: #388e3c;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #666;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4caf50;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #666;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 15px;
  color: #ccc;
}

.btn-criar-empty {
  margin-top: 15px;
  padding: 10px 20px;
  background: #4caf50;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.lembretes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
}

.lembrete-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 20px;
  transition: box-shadow 0.2s, transform 0.2s;
}

.lembrete-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.lembrete-card.inativo {
  opacity: 0.6;
  background: #f9f9f9;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.tipo-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  cursor: pointer;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  border-radius: 24px;
  transition: 0.3s;
}

.toggle-slider::before {
  position: absolute;
  content: '';
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: 0.3s;
}

.toggle-switch input:checked + .toggle-slider {
  background-color: #4caf50;
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(20px);
}

.lembrete-nome {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
}

.lembrete-descricao {
  font-size: 13px;
  color: #666;
  margin: 0 0 12px 0;
  line-height: 1.4;
}

.lembrete-info {
  margin-bottom: 12px;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #555;
  margin-bottom: 6px;
}

.info-row i {
  width: 16px;
  text-align: center;
  color: #888;
}

.stats-hoje {
  display: flex;
  gap: 15px;
  padding: 10px 0;
  border-top: 1px solid #eee;
  margin-bottom: 8px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 600;
}

.stat-item.success {
  color: #388e3c;
}

.stat-item.error {
  color: #c62828;
}

.ultimo-processamento {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #888;
  padding-bottom: 10px;
}

.card-actions {
  display: flex;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}

.btn-action {
  flex: 1;
  padding: 8px;
  border: none;
  border-radius: 6px;
  background: #f5f5f5;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-action:hover {
  background: #e0e0e0;
  color: #333;
}

.btn-action.btn-danger:hover {
  background: #ffebee;
  color: #c62828;
}

/* Dark mode */
.dark-mode .lembrete-card {
  background: #2a2a2a;
  border-color: #444;
}

.dark-mode .lembrete-card.inativo {
  background: #1e1e1e;
}

.dark-mode .lembrete-nome {
  color: #f1f1f1;
}

.dark-mode .lembrete-descricao,
.dark-mode .info-row {
  color: #aaa;
}

.dark-mode .select-filtro {
  background: #2a2a2a;
  border-color: #444;
  color: #f1f1f1;
}

.dark-mode .stats-hoje,
.dark-mode .card-actions {
  border-color: #444;
}

@media (max-width: 768px) {
  .list-header {
    flex-direction: column;
    align-items: stretch;
  }

  .filtros {
    flex-direction: column;
  }

  .select-filtro {
    width: 100%;
  }

  .btn-criar {
    width: 100%;
    justify-content: center;
  }

  .lembretes-grid {
    grid-template-columns: 1fr;
  }
}
</style>
